/**
 * `oidc-client-ts` UserManager for the on-prem Tauri desktop auth flow
 * (MOD-885 tranche 4).
 *
 * Why a separate path from the web (`src/pages/api/auth/[...nextauth].ts`
 * — the catch-all that dispatches to NextAuth when the build profile is
 * on-prem, Kinde otherwise):
 * NextAuth's Keycloak provider relies on Next.js API routes to host the
 * OAuth callback handler. The Tauri build is a static export — there's no
 * Next.js server at runtime — so we use a pure browser-side OIDC client
 * here. Same realm, same client (`scribe-fe`), same PKCE flow.
 *
 * Mirrors the existing Tauri/Kinde split: `src/tauri/kinde.ts` holds
 * Kinde-side constants for the desktop build, this module holds the
 * Keycloak equivalent.
 *
 * ## Storage
 *
 * `oidc-client-ts` defaults `userStore` to `sessionStorage`, which would
 * wipe the token whenever the Tauri webview restarts. We pin it to
 * `localStorage` explicitly so tokens survive restarts — sufficient for
 * the desktop login flow and matches the existing Kinde desktop behaviour.
 * Production-grade secure storage (Tauri's encrypted store, OS keychain)
 * is a follow-up tranche (T6 login UI / T8 build script) — not required
 * for the local-validation smoke goal.
 *
 * ## Redirect URI
 *
 * The deployed Keycloak client allows `https://localhost`, while Tauri serves
 * bundled assets from a platform-specific internal origin (`tauri://localhost`
 * on macOS and `https://tauri.localhost` on Windows). Keep the registered URI
 * here; the on-prem native navigation plugin maps that callback back to the
 * internal app URL before it loads. The callback then runs in the original
 * app origin and can read the pending PKCE transaction from localStorage.
 */
import {
  UserManager,
  WebStorageStateStore,
  type User,
  type UserManagerSettings,
} from 'oidc-client-ts';

import { KEYCLOAK_CLIENT_ID, getRuntimeKeycloakIssuer } from '@/utils/keycloakConfig';

const CALLBACK_PATH = '/onprem/login-callback';
const POST_LOGOUT_PATH = '/';
const FORCE_LOGIN_PROMPT_KEY = 'heidi:onprem:keycloak:force-login-prompt';

const KEYCLOAK_REDIRECT_ORIGIN = 'https://localhost';

function buildSettings(authority = getRuntimeKeycloakIssuer()): UserManagerSettings {
  return {
    authority,
    client_id: KEYCLOAK_CLIENT_ID,
    redirect_uri: `${KEYCLOAK_REDIRECT_ORIGIN}${CALLBACK_PATH}`,
    post_logout_redirect_uri: `${KEYCLOAK_REDIRECT_ORIGIN}${POST_LOGOUT_PATH}`,
    // Pin token storage to localStorage. `oidc-client-ts` defaults
    // `userStore` to `sessionStorage`, which would clear the token on
    // every webview restart — see the storage section in the file
    // docstring for why localStorage is the right call here.
    userStore:
      typeof window !== 'undefined'
        ? new WebStorageStateStore({ store: window.localStorage })
        : undefined,
    response_type: 'code', // PKCE handled automatically
    scope: 'openid profile email',
    // Refresh on a timer slightly before access-token expiry, matching
    // the web (NextAuth) behaviour in MOD-885 T3. This is what unblocks
    // the "refresh works for at least one session-length boundary"
    // acceptance criterion on the Tauri side.
    automaticSilentRenew: true,
    // We don't call `/userinfo` — the access token already carries
    // user_ref_id, region, kinde_user_id, epic_user_id (per the realm
    // config in MOD-884). Saves a round-trip on sign-in.
    loadUserInfo: false,
  };
}

let cachedUserManager: UserManager | null = null;
let cachedAuthority: string | null = null;
let signinCallbackPromise: Promise<User> | null = null;

/**
 * Returns a singleton UserManager. Lazily constructed because the
 * settings depend on browser storage, which isn't available during SSR.
 */
export function getKeycloakUserManager(): UserManager {
  const authority = getRuntimeKeycloakIssuer();
  if (!cachedUserManager || cachedAuthority !== authority) {
    cachedUserManager = new UserManager(buildSettings(authority));
    cachedAuthority = authority;
    // A callback promise belongs to the manager/issuer that created it.
    signinCallbackPromise = null;
  }
  return cachedUserManager;
}

/**
 * Triggers the OIDC PKCE flow. In a Tauri webview this navigates the
 * webview to Keycloak's authorization endpoint; the user authenticates,
 * Keycloak redirects to `/onprem/login-callback`, which calls
 * `completeKeycloakSignIn` to exchange the code for tokens.
 */
export async function startKeycloakSignIn(): Promise<void> {
  // A new authorization request must be allowed to process its own callback.
  // Keep the previous successful callback cached until this point so rerenders
  // cannot consume the same one-time OIDC state twice.
  signinCallbackPromise = null;
  const shouldPromptForFreshLogin =
    typeof window !== 'undefined' &&
    window.localStorage.getItem(FORCE_LOGIN_PROMPT_KEY) === '1';
  if (shouldPromptForFreshLogin) {
    // The flag is cleared only when the callback completes
    // (`completeKeycloakSignIn`), never here: `signinRedirect` can reject
    // before navigating (discovery fetch), and consuming the flag first would
    // let the retry run WITHOUT `prompt=login` — Keycloak's still-alive SSO
    // cookie would then silently sign the next user in as the previous one on
    // a shared workstation.
    await getKeycloakUserManager().signinRedirect({ prompt: 'login' });
    return;
  }
  await getKeycloakUserManager().signinRedirect();
}

/**
 * Completes the OIDC PKCE flow. Called by `/onprem/login-callback`
 * after Keycloak redirects back with the authorization code.
 */
export function completeKeycloakSignIn(): Promise<User> {
  // Authentication updates global app state before this page has necessarily
  // routed away. That can rerender/remount the callback page (for example when
  // the selected-organization hash is added) and invoke this function again.
  // oidc-client-ts removes the pending state on the first successful callback,
  // so a second invocation would incorrectly report "No matching state found".
  signinCallbackPromise ??= getKeycloakUserManager()
    .signinRedirectCallback()
    .then((user) => {
      // Fresh-login enforcement is done only once a login actually lands; a
      // failed or abandoned attempt keeps the flag so the retry still prompts.
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(FORCE_LOGIN_PROMPT_KEY);
      }
      return user;
    })
    .catch((error: unknown) => {
      // A genuine failure must remain retryable.
      signinCallbackPromise = null;
      throw error;
    });
  return signinCallbackPromise;
}

/**
 * Initiates Keycloak's end-session flow. Redirects to Keycloak's
 * logout endpoint, which invalidates the session and redirects back to
 * `/` (the login page).
 */
export async function startKeycloakSignOut(): Promise<void> {
  await getKeycloakUserManager().signoutRedirect();
}

export async function clearKeycloakTauriSession({
  requireFreshLogin = false,
}: {
  requireFreshLogin?: boolean;
} = {}): Promise<void> {
  signinCallbackPromise = null;
  if (requireFreshLogin && typeof window !== 'undefined') {
    window.localStorage.setItem(FORCE_LOGIN_PROMPT_KEY, '1');
  }
  // Desktop logout is local-first — no end-session redirect, because WebView2
  // cannot recover once Keycloak renders an error page. That leaves the
  // server-side session alive, so best-effort revocation kills the refresh and
  // access tokens at Keycloak via a background fetch (no navigation). An
  // unreachable server must not block the local logout; in that case the SSO
  // session dies only by realm timeout (deployment mitigation in MOD-1482).
  try {
    await getKeycloakUserManager().revokeTokens(['refresh_token', 'access_token']);
  } catch {
    // Best-effort only: offline logout still proceeds with local cleanup.
  }
  await getKeycloakUserManager().removeUser();
}

/**
 * Returns the currently signed-in user (or null), without triggering
 * any network calls. `oidc-client-ts` reads from its in-memory cache
 * (backed by localStorage by default).
 */
export async function getKeycloakUser(): Promise<User | null> {
  return getKeycloakUserManager().getUser();
}

/** Test-only: reset the cached UserManager between Jest test isolations. */
export function _resetKeycloakUserManagerForTests(): void {
  cachedUserManager = null;
  cachedAuthority = null;
  signinCallbackPromise = null;
}
